import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Breadcrumbs from '../components/Breadcrumbs';

const ServiceDetailsPage = () => {
    const { clusterName, namespace, serviceName } = useParams();
    const [service, setService] = useState<any>(null);
    const [pods, setPods] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchService = async () => {
            setLoading(true);
            setError(null);
            try {
                // Fetch service details
                const res = await axios.get(`/api/monitoring/clusters/${clusterName}/namespaces/${namespace}/services/${serviceName}`);
                setService(res.data.data);
                // Fetch pods targeted by this service (assuming API provides this)
                const podsRes = await axios.get(`/api/monitoring/clusters/${clusterName}/namespaces/${namespace}/services/${serviceName}/pods`);
                setPods(podsRes.data.data);
            } catch (err: any) {
                setService(null);
                setPods([]);
                setError('Service not found or error fetching service details');
            }
            setLoading(false);
        };
        fetchService();
    }, [clusterName, namespace, serviceName]);

    const navigate = useNavigate();

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading service details...</span>
                </div>
            </div>
        );
    }
    if (!service) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <p className="text-yellow-800 dark:text-yellow-200">{error || 'Service not found'}</p>
                </div>
            </div>
        );
    }
    return (
        <div className="container mx-auto px-4 py-6">
            <Breadcrumbs />
            <div className="flex items-center mb-6">
                <Link to={`/kubernetes/namespaces/${clusterName}/${namespace}`} className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mr-4">
                    ? Back to Namespace
                </Link>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{service.name}</h1>
                <span className={`ml-4 px-3 py-1 text-sm font-semibold rounded-full ${service.type === 'LoadBalancer'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : service.type === 'NodePort'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                    {service.type}
                </span>
            </div>
            {/* Service Info */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Namespace</div>
                    <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">{service.namespace}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">ClusterIP</div>
                    <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">{service.clusterIP || '�'}</div>
                </div>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                    <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Ports</div>
                    <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                        {service.ports && service.ports.length > 0 ? (
                            <div className="space-y-1">
                                {service.ports.map((port: any, idx: number) => (
                                    <div key={idx}>
                                        {port.port}{port.targetPort ? `:${port.targetPort}` : ''}/{port.protocol}
                                    </div>
                                ))}
                            </div>
                        ) : '�'}
                    </div>
                </div>
            </div>
            {/* Pods Table */}
            <div className="mb-8">
                <h2 className="text-lg font-semibold mb-2">Targeted Pods</h2>
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Node</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Restarts</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Age</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {pods.map((pod: any, idx: number) => (
                                <tr
                                    key={pod.name + '-' + idx}
                                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                                    onClick={() => navigate(`/kubernetes/pods/${clusterName}/${namespace}/${pod.name}`)}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">{pod.name}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{pod.status}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{pod.nodeName || '�'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{pod.restartCount ?? '�'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{pod.age || '�'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {pods.length === 0 && (
                        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                            No pods found for this service
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ServiceDetailsPage;
